import { createRoute } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import { FILE_TYPES, PLATFORMS } from '@siliconvault/shared';
import type { VaultTypeDefinition } from '@siliconvault/shared';
import { Shell } from '../components/layout/Shell';
import { useLibraryList, useUploadLibraryFile, useDeleteLibraryFile } from '../hooks/useLibrary';
import { Button } from '../components/ui/Button';
import { Input, Select } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import { useVault } from '../context/VaultContext';
import { usePlatformLabel } from '../hooks/usePlatformLabel';
import { useTranslation } from '../i18n';
import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/library',
  component: LibraryPage,
});

const FILE_ICONS: Record<string, string> = {
  manual_pdf: '[PDF]',
  disk_d64: '[D64]',
  disk_tap: '[TAP]',
  disk_adf: '[ADF]',
  disk_dsk: '[DSK]',
  rom: '[ROM]',
  firmware: '[FW]',
  schematic: '[SCH]',
  other: '[---]',
};

function LibraryPage() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { vaultTypeDef } = useVault();
  const platformLabel = usePlatformLabel();
  const brands = vaultTypeDef ? [...vaultTypeDef.brands] : [...PLATFORMS];
  const fileTypes = vaultTypeDef ? [...vaultTypeDef.fileTypes] : [...FILE_TYPES];
  const [page, setPage] = useState(1);
  const [fileType, setFileType] = useState('');
  const [platform, setPlatform] = useState('');
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  const { data, isLoading } = useLibraryList({
    page,
    limit: 20,
    fileType: fileType || undefined,
    platform: platform || undefined,
    search: search || undefined,
  });

  const uploadMutation = useUploadLibraryFile();
  const deleteMutation = useDeleteLibraryFile();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadForm, setUploadForm] = useState({
    name: '',
    fileType: 'manual_pdf' as string,
    platform: '',
    description: '',
    file: null as File | null,
  });

  async function handleUpload() {
    if (!uploadForm.file || !uploadForm.name) return;

    const buffer = await uploadForm.file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((d, byte) => d + String.fromCharCode(byte), '')
    );

    try {
      await uploadMutation.mutateAsync({
        name: uploadForm.name,
        fileType: uploadForm.fileType,
        data: base64,
        contentType: uploadForm.file.type || 'application/octet-stream',
        platform: uploadForm.platform,
        description: uploadForm.description,
      });
      toast('File uploaded', 'success');
      setShowUpload(false);
      setUploadForm({ name: '', fileType: 'manual_pdf', platform: '', description: '', file: null });
    } catch {
      toast('Failed to upload file', 'error');
    }
  }

  async function handleDelete(fileId: string) {
    try {
      await deleteMutation.mutateAsync(fileId);
      toast('File deleted', 'success');
    } catch {
      toast('Failed to delete file', 'error');
    }
  }

  function formatSize(bytes: number): string {
    const blocks = Math.ceil(bytes / 256);
    return `${blocks} BLOCKS`;
  }

  const files = data?.data ?? [];

  return (
    <Shell>
      <div style={{ maxWidth: '1200px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <h1 style={{ fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.06em' }}>{t('library.title')}</h1>
          <Button size="small" onClick={() => setShowUpload(!showUpload)}>
            {showUpload ? t('library.cancel') : t('library.insertDisk')}
          </Button>
        </div>

        {showUpload && (
          <div style={{
            padding: '1.25rem',
            marginBottom: '1.25rem',
            background: 'var(--bg-secondary)',
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius)',
          }}>
            <div style={{
              textAlign: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.8125rem',
              color: 'var(--text-muted)',
              marginBottom: '1rem',
            }}>
              === INSERT DISK ===
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Input label={t('library.fileName')} value={uploadForm.name} onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })} placeholder={t('library.fileName')} required />
              <Select label={t('library.fileType')} value={uploadForm.fileType} onChange={(e) => setUploadForm({ ...uploadForm, fileType: e.target.value })}>
                {fileTypes.map((ft) => <option key={ft} value={ft}>{ft.replace(/_/g, ' ')}</option>)}
              </Select>
              <Select label={platformLabel} value={uploadForm.platform} onChange={(e) => setUploadForm({ ...uploadForm, platform: e.target.value })}>
                <option value="">{t('library.any')}</option>
                {brands.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
              <Input label={t('library.description')} value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} placeholder={t('library.optionalDescription')} />
            </div>
            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  setUploadForm({ ...uploadForm, file, name: uploadForm.name || file?.name || '' });
                }}
                style={{ display: 'none' }}
              />
              <Button size="small" variant="secondary" onClick={() => fileInputRef.current?.click()}>
                {t('library.chooseFile')}
              </Button>
              <span style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.75rem',
                color: uploadForm.file ? 'var(--text-primary)' : 'var(--text-muted)',
              }}>
                {uploadForm.file ? uploadForm.file.name : t('library.noFileChosen')}
              </span>
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <Button onClick={handleUpload} disabled={!uploadForm.file || !uploadForm.name || uploadMutation.isPending}>
                {uploadMutation.isPending ? t('library.loading') : t('library.loadCommand')}
              </Button>
            </div>
          </div>
        )}

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '1.25rem',
          padding: '0.875rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <Input
              placeholder={t('library.searchPlaceholder')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <Select value={fileType} onChange={(e) => { setFileType(e.target.value); setPage(1); }}>
            <option value="">{t('library.allTypes')}</option>
            {fileTypes.map((ft) => (
              <option key={ft} value={ft}>{ft.replace(/_/g, ' ')}</option>
            ))}
          </Select>
          <Select value={platform} onChange={(e) => { setPlatform(e.target.value); setPage(1); }}>
            <option value="">{`All ${platformLabel}s`}</option>
            {brands.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Select>
        </div>

        {isLoading ? (
          <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{t('library.loadingLibrary')}</div>
        ) : files.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-mono)',
          }}>
            {t('library.readyNoFiles').split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.75rem' }}>
            {files.map((file: any) => (
              <div key={file.id} style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.375rem',
              }}>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '1.25rem',
                  color: 'var(--accent)',
                  marginBottom: '0.25rem',
                }}>
                  {FILE_ICONS[file.fileType] ?? '[---]'}
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{file.name}</div>
                {file.platform && (
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{file.platform}</div>
                )}
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.625rem',
                  color: 'var(--text-muted)',
                }}>
                  {formatSize(file.fileSize)}
                </div>
                <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.25rem' }}>
                  <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <Button size="small" variant="secondary">{t('library.download')}</Button>
                  </a>
                  <Button size="small" variant="danger" onClick={() => handleDelete(file.id)}>{t('library.deleteFile')}</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginTop: '2rem' }}>
            <Button size="small" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('library.prev')}</Button>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {data.page} / {data.totalPages}
            </span>
            <Button size="small" variant="secondary" disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)}>{t('library.next')}</Button>
          </div>
        )}
      </div>
    </Shell>
  );
}
